import { nextTick, watch } from "vue";
import { useDeviceStore } from "~/store/device/deviceStore";

/**
 * Aplica a preferência do coletor aos campos nativos, inclusive os que não
 * usam R3Input/R3InputNumber: `inputmode="none"` evita a abertura do teclado
 * virtual em aparelho com teclado físico.
 *
 * ATENÇÃO — ligar esta preferência **inutiliza a leitura por código de barras**
 * no Urovo CT-48 (medido em 14/08/2026, Chrome 147). O ScanWedge entrega o
 * código pelo IME (`commitText`: `keyCode=229` + `insertText`), e com o teclado
 * desativado só o toque direto no campo abre a sessão de IME — foco programático
 * não abre. Como as telas de coletor encadeiam foco por código, o bipe encadeado
 * não funciona. Para esconder o teclado sem perder a leitura, use um IME
 * invisível no Android e deixe esta preferência desligada.
 */
export default defineNuxtPlugin(() => {
	const deviceStore = useDeviceStore();
	const originalInputModes = new WeakMap<HTMLElement, string | null>();

	function isEditableField(element: Element): element is HTMLInputElement | HTMLTextAreaElement {
		if (!(element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement)) {
			return false;
		}

		return !["button", "checkbox", "file", "hidden", "image", "radio", "reset", "submit"].includes(element.type);
	}

	function applyKeyboardPreference(element: Element) {
		if (!isEditableField(element)) return;

		if (deviceStore.desativarTeclado) {
			if (!originalInputModes.has(element)) {
				originalInputModes.set(element, element.getAttribute("inputmode"));
			}
			if (element.getAttribute("inputmode") !== "none") {
				element.setAttribute("inputmode", "none");
			}
			return;
		}

		if (!originalInputModes.has(element)) return;
		const originalMode = originalInputModes.get(element);
		if (originalMode == null) element.removeAttribute("inputmode");
		else element.setAttribute("inputmode", originalMode);
		originalInputModes.delete(element);
	}

	function applyToAllFields() {
		document.querySelectorAll("input, textarea").forEach(applyKeyboardPreference);
	}

	watch(
		() => deviceStore.desativarTeclado,
		async () => {
			applyToAllFields();
			await nextTick();
			applyToAllFields();
		},
		{ immediate: true },
	);

	const observer = new MutationObserver((mutations) => {
		for (const mutation of mutations) {
			if (mutation.type === "attributes") {
				if (mutation.target instanceof Element) {
					applyKeyboardPreference(mutation.target);
				}
				continue;
			}

			mutation.addedNodes.forEach((node) => {
				if (!(node instanceof Element)) return;
				applyKeyboardPreference(node);
				node.querySelectorAll("input, textarea").forEach(applyKeyboardPreference);
			});
		}
	});

	// Em navegadores o observer sempre possui `observe`. A guarda também evita
	// que um ambiente parcial de teste interrompa a inicialização da aplicação.
	if (typeof observer.observe === "function") {
		observer.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ["inputmode"],
		});
	}
});
